/**
 * "حركات رأس المال" (C26): the dashboard capital card opens the history dialog, as the old `handleShowCapitalHistory`
 * did (App.tsx:9028-9044 → `HistoryModal` with the capital block :50580-50610), over `GET /capital/operations` — every
 * visible operation that touched capital plus the profit withdrawals (services/dashboard.ts). "وارد" = capital up or
 * cash in, "صادر" = capital down or cash out (a capitalization moves no cash; a profit withdrawal moves cash out
 * without changing capital). The add / withdraw / capitalize buttons open the shared capital dialogs. Print = the A4
 * statement through the browser (PDF the same until the B5 worker, as C19 – C25). Display only.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { HistoryModal, type HistoryFilter, type HistoryRow } from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import type { CapitalOperationRow } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { isSessionLost } from "../../app/errors.js";
import { keys } from "../../app/query.js";
import { officeDate } from "../wallets/StatementContainer.js";
import { fmt, isZero } from "../customers/format.js";
import type { CapitalActions } from "./CapitalActions.js";
import { usePrintOffice } from "../../app/printOffice.js";

/** The dialog's type filter: "deposit" = capital in, "withdraw" = capital or profit out; the other old options match nothing here. */
const IN_TYPES = new Set(["capital_add", "profit_capitalize", "usd_direct_deposit", "currency_direct_deposit", "opening_balance", "currency_opening_balance"]);

function hourOf(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", hour12: false }).format(new Date(iso)).slice(0, 2);
}

export function CapitalHistoryContainer({ actions, onClose }: { actions: CapitalActions; onClose: () => void }) {
  const { source, t, locale, now } = useApp();
  const { session, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const tz = session.office.timeZone;
  const [filter, setFilter] = useState<HistoryFilter>({ type: "", from: "", to: "", operator: "", hour: "" });
  const [printing, setPrinting] = useState(false);
  const query = useQuery({ queryKey: keys.capitalOperations, queryFn: () => source.capitalOperations() });
  if (query.error && isSessionLost(query.error)) sessionLost();
  const unit = t("unit.iqd");

  const cashPart = (cash: Readonly<Record<string, string>>, sign: "in" | "out"): string[] =>
    Object.entries(cash)
      .filter(([, v]) => (sign === "in" ? !v.startsWith("-") : v.startsWith("-")) && !isZero(v))
      .map(([c, v]) => (c === "IQD" ? `${fmt(v.replace(/^-/, ""))} ${unit}` : `${fmt(v.replace(/^-/, ""))} ${c === "USD" ? "$" : c}`));

  const all: readonly CapitalOperationRow[] = query.data?.rows ?? [];
  const rows: HistoryRow[] = useMemo(() => {
    return all
      .filter((r) => {
        if (filter.from && r.officeDate < filter.from) return false;
        if (filter.to && r.officeDate > filter.to) return false;
        if (filter.type === "deposit" && !IN_TYPES.has(r.type)) return false;
        if (filter.type === "withdraw" && IN_TYPES.has(r.type)) return false;
        if (filter.type && filter.type !== "deposit" && filter.type !== "withdraw") return false;
        if (filter.operator && r.operator !== filter.operator) return false;
        if (filter.hour && hourOf(r.postedAt, tz) !== filter.hour) return false;
        return true;
      })
      .map((r): HistoryRow => {
        const capUp = !isZero(r.capital) && !r.capital.startsWith("-");
        const capDown = r.capital.startsWith("-");
        // the column follows the capital effect; a profit withdrawal (capital unchanged) shows the cash that left
        const inParts = capUp ? (r.type === "profit_capitalize" ? [`${fmt(r.capital)} ${unit}`] : cashPart(r.cash, "in").length ? cashPart(r.cash, "in") : [`${fmt(r.capital)} ${unit}`]) : [];
        const outParts = capDown ? (cashPart(r.cash, "out").length ? cashPart(r.cash, "out") : [`${fmt(r.capital.replace(/^-/, ""))} ${unit}`]) : isZero(r.capital) ? cashPart(r.cash, "out") : [];
        const typeLabel = t(`jr.type.${r.type}`);
        return {
          id: r.id,
          dateLabel: r.officeDate,
          timeLabel: new Date(r.postedAt).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: tz }),
          typeKey: r.type,
          typeLabel: typeLabel === `jr.type.${r.type}` ? r.type : typeLabel,
          badgeTone: capUp ? "emerald" : "rose",
          description: r.box ?? "-",
          note: r.note ?? "-",
          operator: r.operator ?? "",
          inLabel: inParts.length ? inParts.join(" / ") : "-",
          outLabel: outParts.length ? outParts.join(" / ") : "-",
          commissionLabel: "-",
          balanceLabel: "",
          balanceUsdLabel: "",
        };
      });
  }, [all, filter, t, tz, unit]);

  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);

  const title = t("cap.history_title");
  return (
    <>
      <HistoryModal
        variant="movements"
        title={title}
        rows={rows}
        filter={filter}
        onFilterChange={(patch) => setFilter((f) => ({ ...f, ...patch }))}
        onRowDetails={() => undefined}
        onPrint={() => setPrinting(true)}
        onExportPdf={() => setPrinting(true)}
        onClose={onClose}
        loading={query.isLoading}
        emptyState={all.length === 0 ? "no_movements" : "no_match"}
        capitalActions={{
          ...(actions.canManage ? { onAdd: actions.openAdd, onWithdraw: actions.openWithdraw } : {}),
          ...(actions.canCapitalize ? { onCapitalize: actions.openCapitalize } : {}),
        }}
      />
      {printing &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={t("history.movements_title", { title })}
              customerName={session.office.name}
              dateLabel={officeDate(now(), tz)}
              docNumber=""
              office={printOffice}
              {...(filter.from ? { periodFrom: filter.from } : {})}
              {...(filter.to ? { periodTo: filter.to } : {})}
              columns={[
                { header: t("date"), key: "date" },
                { header: t("history.category"), key: "category" },
                { header: t("description"), key: "description" },
                { header: t("note"), key: "note" },
                { header: t("incoming"), key: "incoming" },
                { header: t("outgoing"), key: "outgoing" },
              ]}
              rows={rows.map((r) => ({ date: `${r.dateLabel} ${r.timeLabel}`, category: r.typeLabel, description: r.description, note: r.note, incoming: r.inLabel, outgoing: r.outLabel }))}
              summary={[]}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
